import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Download, Loader2, Plus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BatchTransactionDetailSheet as TransactionDetailSheet } from '@/components/transactions/batch-transaction-detail-sheet';
import { transactionChildWithContext } from '@mls-wallet/shared-types';
import { StatusBadge } from '@/components/shared/status-badge';
import {
  OriginDisplay,
  DestinationDisplay,
} from '@/components/transactions/transaction-displays';
import { TransactionTypeDisplay } from '@/components/transactions/transaction-type-display';
import {
  formatTransactionDate,
  getHistoricTransactionSortTimestamp,
  getTransactionExpiryDisplay,
  type TransactionExpiryTone,
  isTransactionExpired,
  resolveSelectedTransaction,
} from '@/components/transactions/utils';
import {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
  StyledTableCell,
} from '@/components/ui/styled-table';
import {
  PageHeader,
  PageTitle,
  PageDescription,
  PageAction,
  PageHeaderInfo,
  PageContent,
} from '@/components/layout/page-header';
import { useQueuedTransactions } from '@/hooks/transaction/use-queued-transactions';
import { useHistoricTransactions } from '@/hooks/transaction/use-historic-transactions';
import { useAddressBookEntriesByAddresses } from '@/hooks/organization/use-address-book-entries-by-addresses';
import { ExportTransactionsDialog } from '@/components/transactions/export-transactions-dialog';
import {
  IndexedTokenMovementRow,
  IndexedTokenMovementSheet,
} from '@/components/transactions/indexed-token-movement';
import { useExpiredHistoricTransactionCount } from '@/hooks/transaction/use-expired-historic-transaction-count';
import { useCreateTransactionSignature } from '@/hooks/transaction/use-create-transaction-signature';
import { useDismissTransaction } from '@/hooks/transaction/use-dismiss-transaction';
import { useAutoLoadMore } from '@/hooks/use-auto-load-more';
import {
  AccountTransaction,
  AccountTransactionDecision,
  SignableType,
  type TransactionHistoryItem,
} from '@mls-wallet/shared-types';
import { cn } from '@/lib/utils';
import { SIGNABLE_POLL_INTERVAL_MS } from '@/components/execution/execution-polling';
import { shouldPollHistoricTransactions } from '@/components/transactions/transaction-execution-state';

const TRANSACTION_TABLE_COLUMN_WIDTHS = [
  'w-[26%]',
  'w-[17%]',
  'w-[17%]',
  'w-[8%]',
  'w-[10%]',
  'w-[18%]',
  'w-[4%]',
] as const;

type TransactionListSource = 'queued' | 'historic';

function TransactionTableColGroup() {
  return (
    <colgroup>
      {/* Keep queued and historic on the same column grid so tab switches do
      not cause the table to jump. These widths are tuned against long
      descriptions, contract labels, and address-book names. */}
      {TRANSACTION_TABLE_COLUMN_WIDTHS.map((width, index) => (
        <col key={`${width}-${index}`} className={width} />
      ))}
    </colgroup>
  );
}

export function TransactionsPage() {
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  const [selectedTransactionSource, setSelectedTransactionSource] =
    useState<TransactionListSource | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedIndexedMovementId, setSelectedIndexedMovementId] = useState<
    string | null
  >(null);
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());
  const [currentTimeMs, setCurrentTimeMs] = useState(() => Date.now());
  const [showExpiredHistoric, setShowExpiredHistoric] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const queuedLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const historicLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const currentTab =
    searchParams.get('tab') === 'historic' ? 'historic' : 'queued';
  const shouldPollHistoric = shouldPollHistoricTransactions(
    currentTab,
    isSheetOpen
  );

  const {
    transactions: queuedTransactions,
    pagination: queuedPagination,
    refetch: refetchQueued,
    loadMore: loadMoreQueued,
    hasMore: queuedHasMore,
    isLoading: isLoadingQueued,
    isLoadingMore: isLoadingMoreQueued,
    reset: resetQueued,
  } = useQueuedTransactions({
    // Historic can derive its expired rows from queued data, so keep the
    // queued feed enabled and polling while this page is mounted.
    enabled: true,
    refreshInterval: SIGNABLE_POLL_INTERVAL_MS,
    // A selected row can come from any loaded page. Refresh every loaded page
    // while its sheet is open so cross-user execution is not hidden in page 2+.
    revalidateAll: isSheetOpen && selectedTransactionSource === 'queued',
  });
  const {
    transactions: historicTransactions,
    indexedMovements: historicMovements = [],
    pagination: historicPagination,
    refetch: refetchHistoric,
    loadMore: loadMoreHistoric,
    hasMore: historicHasMore,
    isLoading: isLoadingHistoric,
    isLoadingMore: isLoadingMoreHistoric,
    reset: resetHistoric,
    error: historicError,
  } = useHistoricTransactions({
    enabled: shouldPollHistoric,
    refreshInterval: shouldPollHistoric ? SIGNABLE_POLL_INTERVAL_MS : 0,
  });
  const { entryByAddress: historicAddressBookEntries } =
    useAddressBookEntriesByAddresses(
      currentTab === 'historic'
        ? historicMovements.flatMap(({ fromAddress, toAddress }) => [
            fromAddress,
            toAddress,
          ])
        : []
    );
  const {
    expiredTransactionCount: expiredHistoricTransactionCount,
    refetch: refetchExpiredHistoricTransactionCount,
  } = useExpiredHistoricTransactionCount({
    enabled: currentTab === 'historic',
    refreshInterval: currentTab === 'historic' ? SIGNABLE_POLL_INTERVAL_MS : 0,
  });

  const visibleQueuedTransactions = queuedTransactions.filter(
    (tx) => !isTransactionExpired(tx, currentTimeMs)
  );
  const expiredQueuedTransactions = queuedTransactions.filter((tx) =>
    isTransactionExpired(tx, currentTimeMs)
  );
  const selectedTransaction = useMemo(() => {
    const resolvedTransaction = resolveSelectedTransaction(
      selectedTransactionId,
      queuedTransactions,
      historicTransactions,
      currentTimeMs
    );
    if (resolvedTransaction || selectedTransactionSource !== 'historic') {
      return resolvedTransaction;
    }

    // A row opened from Historic can use an explicitly expired local copy until
    // the backend historic poll returns its authoritative replacement. A row
    // opened from Queued intentionally receives no fallback and closes at expiry.
    const locallyExpiredTransaction = queuedTransactions.find(
      (transaction) =>
        transaction.id === selectedTransactionId &&
        isTransactionExpired(transaction, currentTimeMs)
    );
    return locallyExpiredTransaction
      ? { ...locallyExpiredTransaction, signatureStatus: 'expired' as const }
      : null;
  }, [
    currentTimeMs,
    historicTransactions,
    queuedTransactions,
    selectedTransactionId,
    selectedTransactionSource,
  ]);

  const refreshAllTransactions = useCallback(async (): Promise<void> => {
    await Promise.all([
      refetchQueued(),
      refetchHistoric(),
      refetchExpiredHistoricTransactionCount(),
    ]);
  }, [refetchExpiredHistoricTransactionCount, refetchHistoric, refetchQueued]);

  useAutoLoadMore(
    queuedLoadMoreRef,
    currentTab === 'queued',
    queuedHasMore,
    isLoadingMoreQueued,
    queuedPagination.cursor,
    loadMoreQueued
  );
  useAutoLoadMore(
    historicLoadMoreRef,
    currentTab === 'historic',
    historicHasMore,
    isLoadingMoreHistoric,
    historicPagination.cursor,
    loadMoreHistoric
  );

  // Transaction signature hook
  const { createSignature } = useCreateTransactionSignature({
    onSuccess: () => {
      void refreshAllTransactions();
    },
  });

  // Dismiss transaction hook
  const { dismissTransaction } = useDismissTransaction({
    onSuccess: () => {
      // Refresh queued transactions after dismissal
      void refetchQueued();
    },
  });

  // Clean up dismissingIds when transactions are removed from the list
  // This prevents memory leaks from IDs accumulating after successful dismissals
  useEffect(() => {
    if (dismissingIds.size === 0) return;

    const currentIds = new Set(visibleQueuedTransactions.map((tx) => tx.id));
    const staleIds = [...dismissingIds].filter((id) => !currentIds.has(id));

    if (staleIds.length > 0) {
      setDismissingIds((prev) => {
        const next = new Set(prev);
        staleIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [visibleQueuedTransactions, dismissingIds]);

  // Keep the 1-second clock running whenever queued rows exist so expiration
  // transitions continue to move rows from queued into historic even while the
  // user is already looking at the historic tab.
  useEffect(() => {
    if (queuedTransactions.length === 0) {
      return;
    }

    setCurrentTimeMs(Date.now());

    const intervalId = window.setInterval(() => {
      setCurrentTimeMs(Date.now());
    }, 1_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [queuedTransactions.length]);

  useEffect(() => {
    if (expiredHistoricTransactionCount === 0 && showExpiredHistoric) {
      setShowExpiredHistoric(false);
    }
  }, [expiredHistoricTransactionCount, showExpiredHistoric]);

  useEffect(() => {
    // Case: Polling removed the selection, or the live clock expired its
    // queued row.
    if (
      !isSheetOpen ||
      selectedTransactionId === null ||
      selectedTransaction !== null
    ) {
      return;
    }

    setIsSheetOpen(false);
    setSelectedTransactionId(null);
    setSelectedTransactionSource(null);
  }, [isSheetOpen, selectedTransaction, selectedTransactionId]);

  const handleTabChange = (value: string) => {
    if (value === currentTab) {
      return;
    }
    setSelectedIndexedMovementId(null);

    // Switching tabs resets both lists back to page one so each tab always
    // re-enters from the beginning instead of resuming a partially loaded feed.
    void resetQueued();
    void resetHistoric();

    // Mirror the selected tab into the URL so page refreshes and deep links
    // preserve whether the user was looking at queued or historic rows.
    setSearchParams(value === 'historic' ? { tab: 'historic' } : {}, {
      replace: true,
    });
  };

  const handleRowClick = (
    transaction: AccountTransaction,
    source: TransactionListSource
  ) => {
    setSelectedIndexedMovementId(null);
    setSelectedTransactionId(transaction.id);
    setSelectedTransactionSource(source);
    setIsSheetOpen(true);
  };

  const handleApproveTransaction = (transaction: AccountTransaction) =>
    createSignature({
      transactionId: transaction.id,
      decision: AccountTransactionDecision.APPROVE,
    });
  const handleRejectTransaction = (
    transaction: AccountTransaction,
    reason: string
  ) =>
    createSignature({
      transactionId: transaction.id,
      decision: AccountTransactionDecision.REJECT,
      reason,
    });

  const handleDismissTransaction = useCallback(
    async (e: React.MouseEvent, transactionId: string) => {
      e.stopPropagation(); // Prevent row click from opening the detail sheet

      // Add to dismissing set to trigger animation
      setDismissingIds((prev) => new Set(prev).add(transactionId));

      // Wait for animation to complete before actually dismissing
      await new Promise((resolve) => setTimeout(resolve, 300));

      try {
        await dismissTransaction({ transactionId });
        // Clean up dismissingIds on success
        setDismissingIds((prev) => {
          const next = new Set(prev);
          next.delete(transactionId);
          return next;
        });
      } catch (error) {
        console.error('Failed to dismiss transaction:', error);
        // Remove from dismissing set on error
        setDismissingIds((prev) => {
          const next = new Set(prev);
          next.delete(transactionId);
          return next;
        });
      }
    },
    [dismissTransaction]
  );

  const resolveDisplayStatus = (tx: AccountTransaction) =>
    tx.executionStatus ?? tx.signatureStatus;

  // The backend historic feed includes expired rows after polling catches up,
  // but we also derive them locally from queued so the historic tab updates
  // immediately when a row expires on-screen. Dedupe by id so a row never
  // renders twice once both sources include it.
  const combinedHistoricTransactions = [
    ...historicTransactions,
    ...expiredQueuedTransactions.filter(
      (queuedTx) =>
        !historicTransactions.some(
          (historicTx) => historicTx.id === queuedTx.id
        )
    ),
  ].sort(
    (left, right) =>
      getHistoricTransactionSortTimestamp(right) -
      getHistoricTransactionSortTimestamp(left)
  );
  const visibleHistoricTransactions = combinedHistoricTransactions.filter(
    (tx) => showExpiredHistoric || resolveDisplayStatus(tx) !== 'expired'
  );
  const hiddenExpiredHistoricCount = showExpiredHistoric
    ? 0
    : expiredHistoricTransactionCount;
  const visibleIndexedMovements = historicMovements;
  const selectedIndexedMovement =
    currentTab === 'historic'
      ? (visibleIndexedMovements.find(
          ({ id }) => id === selectedIndexedMovementId
        ) ?? null)
      : null;
  const visibleHistoricItems: TransactionHistoryItem[] = [
    ...visibleHistoricTransactions.map((transaction) => ({
      kind: 'proposal' as const,
      id: 'proposal:' + transaction.id,
      transaction,
    })),
    ...visibleIndexedMovements.map((movement) => ({
      kind: 'movement' as const,
      id: movement.id,
      movement,
    })),
  ];
  const historicTimestamp = (item: TransactionHistoryItem) =>
    item.kind === 'proposal'
      ? getHistoricTransactionSortTimestamp(item.transaction)
      : Date.parse(item.movement.executedAt);
  visibleHistoricItems.sort((left, right) => {
    const timestampOrder = historicTimestamp(right) - historicTimestamp(left);
    if (timestampOrder) return timestampOrder;
    // Reciprocal transfers share an execution time; show Received above Sent.
    const leftReceived =
      left.kind === 'movement' && left.movement.direction === 'received';
    const rightReceived =
      right.kind === 'movement' && right.movement.direction === 'received';
    return leftReceived === rightReceived ? 0 : leftReceived ? -1 : 1;
  });

  const getExpiryBadgeClassName = (tone: TransactionExpiryTone) => {
    switch (tone) {
      case 'warning':
        return 'border-red-300 bg-red-20 text-red-800 dark:bg-red-800/20 dark:text-red-400';
      default:
        return 'border-border/80 text-foreground/85';
    }
  };

  return (
    <div className="">
      <PageHeader>
        <PageHeaderInfo>
          <PageTitle>Transactions</PageTitle>
          <PageDescription>View and manage your transactions</PageDescription>
        </PageHeaderInfo>
        <PageAction className="flex items-center gap-3">
          {currentTab === 'historic' && (
            <Button
              variant="outline"
              onClick={() => setIsExportDialogOpen(true)}
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          )}
          <Button onClick={() => navigate('/new-transaction')}>
            <Plus className="h-4 w-4 mr-1" />
            New Transaction
          </Button>
        </PageAction>
      </PageHeader>

      <PageContent>
        <Tabs
          value={currentTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <div className="mb-6 flex items-center justify-between gap-4">
            <TabsList className="flex flex-row items-stretch border-border border text-xs w-fit p-0">
              <TabsTrigger value="queued" className="text-xs">
                Queued
              </TabsTrigger>
              <TabsTrigger value="historic" className="text-xs">
                Historic
              </TabsTrigger>
            </TabsList>

            {currentTab === 'historic' &&
            !isLoadingHistoric &&
            expiredHistoricTransactionCount > 0 ? (
              <Label
                htmlFor="show-expired-historic"
                className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground"
              >
                <Checkbox
                  id="show-expired-historic"
                  checked={showExpiredHistoric}
                  onCheckedChange={(checked) =>
                    setShowExpiredHistoric(checked === true)
                  }
                />
                Show expired transactions
                {` (${expiredHistoricTransactionCount})`}
              </Label>
            ) : (
              <div />
            )}
          </div>

          <TabsContent value="queued" className="mt-0">
            {isLoadingQueued && (
              <div className="h-48 flex items-center justify-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading
                transactions...
              </div>
            )}

            {!isLoadingQueued && visibleQueuedTransactions.length === 0 && (
              <div className="h-48 flex items-center justify-center text-slate-500">
                You&apos;re all caught up. New transactions will appear here
                when they need to be approved or executed.
              </div>
            )}

            {!isLoadingQueued && visibleQueuedTransactions.length > 0 && (
              <>
                <StyledTableWrapper>
                  <StyledTable className="table-fixed min-w-[980px]">
                    <TransactionTableColGroup />
                    <StyledTableHeader>
                      <tr>
                        <StyledTableHead>Transaction</StyledTableHead>
                        <StyledTableHead>From</StyledTableHead>
                        <StyledTableHead>To</StyledTableHead>
                        <StyledTableHead className="pr-6">
                          Created
                        </StyledTableHead>
                        <StyledTableHead className="pr-6">
                          Expires
                        </StyledTableHead>
                        <StyledTableHead>Status</StyledTableHead>
                        <StyledTableHead></StyledTableHead>
                      </tr>
                    </StyledTableHeader>
                    <StyledTableBody>
                      {visibleQueuedTransactions.map((tx) => {
                        const isDismissing = dismissingIds.has(tx.id);
                        return (
                          <StyledTableRow
                            key={tx.id}
                            onClick={() => handleRowClick(tx, 'queued')}
                            className={cn(
                              'cursor-pointer transition-all duration-300 [&>td]:py-3.5',
                              isDismissing &&
                                'opacity-0 scale-95 h-0 overflow-hidden'
                            )}
                          >
                            <StyledTableCell>
                              {tx.children.length === 1 && tx.children[0] ? (
                                <TransactionTypeDisplay
                                  transaction={transactionChildWithContext(
                                    tx,
                                    tx.children[0]
                                  )}
                                />
                              ) : (
                                <div className="flex min-w-0 items-start gap-2">
                                  <Box
                                    aria-hidden
                                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                                  />
                                  <div className="min-w-0 space-y-1">
                                    <p className="text-base font-semibold leading-tight">
                                      Bulk transaction ({tx.children.length}{' '}
                                      transactions)
                                    </p>
                                    <p className="truncate text-xs font-normal text-muted-foreground">
                                      {tx.description}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </StyledTableCell>
                            <StyledTableCell>
                              <OriginDisplay account={tx.account} />
                            </StyledTableCell>
                            <StyledTableCell>
                              {tx.children.length === 1 && tx.children[0] ? (
                                (() => {
                                  const child = tx.children[0];
                                  return child.type ===
                                    SignableType.TokenTransfer ? (
                                    <DestinationDisplay
                                      type={child.type}
                                      destination={child.destination}
                                      destinationName={child.destinationName}
                                      networkId={tx.networkId}
                                    />
                                  ) : (
                                    <DestinationDisplay
                                      type={child.type}
                                      contractAddress={child.toAddress}
                                      contractAddressName={
                                        child.contractAddressName
                                      }
                                      networkId={tx.networkId}
                                    />
                                  );
                                })()
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  {tx.children.length} transactions
                                </span>
                              )}
                            </StyledTableCell>
                            <StyledTableCell className="whitespace-nowrap pr-6 text-sm font-medium text-foreground/80">
                              {formatTransactionDate(tx.dateProposed)}
                            </StyledTableCell>
                            <StyledTableCell className="pr-6">
                              {(() => {
                                const expiryDisplay =
                                  getTransactionExpiryDisplay(
                                    tx.expirationTimestamp,
                                    currentTimeMs
                                  );

                                if (!expiryDisplay) {
                                  return null;
                                }

                                return (
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      'w-[6.25rem] justify-center whitespace-nowrap px-1 py-0.5 text-[12px] font-medium tabular-nums',
                                      getExpiryBadgeClassName(
                                        expiryDisplay.tone
                                      )
                                    )}
                                  >
                                    {expiryDisplay.label}
                                  </Badge>
                                );
                              })()}
                            </StyledTableCell>
                            <StyledTableCell>
                              {resolveDisplayStatus(tx) ===
                                'pendingReviewSignatures' &&
                              tx.children.length === 1 &&
                              tx.children[0] ? (
                                <StatusBadge
                                  status={resolveDisplayStatus(tx)}
                                  invalidated={tx.invalidated}
                                  currentApprovals={
                                    tx.children[0].approvedMembers.length
                                  }
                                  requiredApprovals={
                                    tx.children[0].policy.requiredApprovals
                                  }
                                  className="whitespace-nowrap px-2.5 py-0.5 text-[12px] font-medium"
                                />
                              ) : (
                                <StatusBadge
                                  status={resolveDisplayStatus(tx)}
                                  invalidated={tx.invalidated}
                                  className="whitespace-nowrap px-2.5 py-0.5 text-[12px] font-medium"
                                />
                              )}
                            </StyledTableCell>
                            <StyledTableCell className="text-right">
                              {tx.invalidated && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"
                                  onClick={(e) =>
                                    handleDismissTransaction(e, tx.id)
                                  }
                                  disabled={isDismissing}
                                >
                                  Dismiss
                                </Button>
                              )}
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                    </StyledTableBody>
                  </StyledTable>
                </StyledTableWrapper>
                {(queuedHasMore || isLoadingMoreQueued) && (
                  <div
                    ref={queuedLoadMoreRef}
                    className="flex min-h-12 items-center justify-center py-4"
                  >
                    {isLoadingMoreQueued ? (
                      <Loader2
                        className="h-5 w-5 animate-spin text-muted-foreground"
                        aria-label="Loading more queued transactions"
                      />
                    ) : null}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="historic" className="mt-0">
            {historicError && (
              <div
                role="alert"
                className="mb-4 flex items-center gap-3 text-sm text-destructive"
              >
                Unable to load transaction history.
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void refetchHistoric();
                  }}
                >
                  Retry
                </Button>
              </div>
            )}
            {isLoadingHistoric && (
              <div className="h-48 flex items-center justify-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading
                transactions...
              </div>
            )}

            {!isLoadingHistoric &&
              !historicError &&
              visibleHistoricItems.length === 0 && (
                <div className="h-48 flex items-center justify-center text-slate-500">
                  {hiddenExpiredHistoricCount > 0
                    ? 'No non-expired historic transactions are visible. Turn on Show expired transactions to include expired items.'
                    : 'No historic transactions yet. Completed transactions and token movements will appear here.'}
                </div>
              )}

            {!isLoadingHistoric && visibleHistoricItems.length > 0 && (
              <>
                <StyledTableWrapper>
                  <StyledTable className="table-fixed min-w-[980px]">
                    <TransactionTableColGroup />
                    <StyledTableHeader>
                      <tr>
                        <StyledTableHead>Transaction</StyledTableHead>
                        <StyledTableHead>From</StyledTableHead>
                        <StyledTableHead>To</StyledTableHead>
                        <StyledTableHead className="pr-6">
                          Created
                        </StyledTableHead>
                        <StyledTableHead>Executed</StyledTableHead>
                        <StyledTableHead>Status</StyledTableHead>
                        <StyledTableHead></StyledTableHead>
                      </tr>
                    </StyledTableHeader>
                    <StyledTableBody>
                      {visibleHistoricItems.map((item) => {
                        if (item.kind === 'movement') {
                          return (
                            <IndexedTokenMovementRow
                              key={item.id}
                              movement={item.movement}
                              addressBookEntryByAddress={
                                historicAddressBookEntries
                              }
                              onOpen={() => {
                                setIsSheetOpen(false);
                                setSelectedIndexedMovementId(item.movement.id);
                              }}
                            />
                          );
                        }
                        const tx = item.transaction;
                        const displayStatus = resolveDisplayStatus(tx);

                        return (
                          <StyledTableRow
                            key={tx.id}
                            onClick={() => handleRowClick(tx, 'historic')}
                            className="cursor-pointer [&>td]:py-3.5"
                          >
                            <StyledTableCell>
                              {tx.children.length === 1 && tx.children[0] ? (
                                <TransactionTypeDisplay
                                  transaction={transactionChildWithContext(
                                    tx,
                                    tx.children[0]
                                  )}
                                />
                              ) : (
                                <div className="flex min-w-0 items-start gap-2">
                                  <Box
                                    aria-hidden
                                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                                  />
                                  <div className="min-w-0 space-y-1">
                                    <p className="text-base font-semibold leading-tight">
                                      Bulk transaction ({tx.children.length}{' '}
                                      transactions)
                                    </p>
                                    <p className="truncate text-xs font-normal text-muted-foreground">
                                      {tx.description}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </StyledTableCell>
                            <StyledTableCell>
                              <OriginDisplay account={tx.account} />
                            </StyledTableCell>
                            <StyledTableCell>
                              {tx.children.length === 1 && tx.children[0] ? (
                                (() => {
                                  const child = tx.children[0];
                                  return child.type ===
                                    SignableType.TokenTransfer ? (
                                    <DestinationDisplay
                                      type={child.type}
                                      destination={child.destination}
                                      destinationName={child.destinationName}
                                      networkId={tx.networkId}
                                    />
                                  ) : (
                                    <DestinationDisplay
                                      type={child.type}
                                      contractAddress={child.toAddress}
                                      contractAddressName={
                                        child.contractAddressName
                                      }
                                      networkId={tx.networkId}
                                    />
                                  );
                                })()
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  {tx.children.length} transactions
                                </span>
                              )}
                            </StyledTableCell>
                            <StyledTableCell className="whitespace-nowrap pr-6 text-sm font-medium text-foreground/80">
                              {formatTransactionDate(tx.dateProposed)}
                            </StyledTableCell>
                            <StyledTableCell className="whitespace-nowrap text-sm font-medium text-foreground/80">
                              {displayStatus === 'completed' && tx.dateExecuted
                                ? formatTransactionDate(tx.dateExecuted)
                                : '--'}
                            </StyledTableCell>
                            <StyledTableCell>
                              <StatusBadge
                                status={displayStatus}
                                invalidated={tx.invalidated}
                                className="whitespace-nowrap px-2.5 py-0.5 text-[12px] font-medium"
                              />
                            </StyledTableCell>
                            <StyledTableCell className="text-center">
                              {/* Historic transactions don't need action badges */}
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                    </StyledTableBody>
                  </StyledTable>
                </StyledTableWrapper>
                {(historicHasMore || isLoadingMoreHistoric) && (
                  <div
                    ref={historicLoadMoreRef}
                    className="flex min-h-12 items-center justify-center py-4"
                  >
                    {isLoadingMoreHistoric ? (
                      <Loader2
                        className="h-5 w-5 animate-spin text-muted-foreground"
                        aria-label="Loading more historic transactions"
                      />
                    ) : null}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Signing polls the queued feed even when the historic tab is selected. */}
        <TransactionDetailSheet
          transaction={selectedTransaction}
          transactions={queuedTransactions}
          refreshQueuedTransactions={refetchQueued}
          refreshAllTransactions={refreshAllTransactions}
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          onApprove={handleApproveTransaction}
          onReject={handleRejectTransaction}
        />
        <ExportTransactionsDialog
          open={isExportDialogOpen}
          onOpenChange={setIsExportDialogOpen}
        />
        <IndexedTokenMovementSheet
          movement={selectedIndexedMovement}
          addressBookEntryByAddress={historicAddressBookEntries}
          onClose={() => setSelectedIndexedMovementId(null)}
        />
      </PageContent>
    </div>
  );
}
